<template>
  <div class="profile-page">
    <div class="profile-shell">
      <section class="profile-hero">
        <div>
          <p class="profile-eyebrow">Account Settings</p>
          <h1>User profile</h1>
          <p class="profile-copy">
            Review your account details, update your sign-in information, and keep your admin
            profile current.
          </p>
        </div>

        <div class="profile-hero__badge">
          <span>Signed in as</span>
          <strong>{{ displayValue(profileSummary.fullName || profileSummary.username) }}</strong>
        </div>
      </section>

      <div v-if="isLoading" class="loading-shell">
        <div class="spinner-border load-icon" role="status"></div>
        <p>Loading profile...</p>
      </div>

      <div v-else class="profile-grid">
        <section class="profile-card">
          <div class="section-heading">
            <p class="section-kicker">Overview</p>
            <h2>User information</h2>
          </div>

          <div class="info-grid">
            <article class="info-item">
              <span class="info-item__label">Full name</span>
              <strong>{{ displayValue(profileSummary.fullName) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Role</span>
              <strong>{{ displayValue(profileSummary.role) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Organization</span>
              <strong>{{ displayValue(profileSummary.organization) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Organization Type</span>
              <strong>{{ displayValue(profileSummary.organizationType) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Phone Number</span>
              <strong>{{ displayValue(profileSummary.phoneNumber) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Country</span>
              <strong>{{ displayValue(profileSummary.country) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">State</span>
              <strong>{{ displayValue(profileSummary.state) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Geo Zone</span>
              <strong>{{ displayValue(profileSummary.geoZone) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Status</span>
              <strong>{{ displayValue(profileSummary.status) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Username</span>
              <strong>{{ displayValue(profileSummary.username) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Email</span>
              <strong>{{ displayValue(profileSummary.email) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">File Number</span>
              <strong>{{ displayValue(profileSummary.fileNumber) }}</strong>
            </article>
            <article class="info-item">
              <span class="info-item__label">Establishment Year</span>
              <strong>{{ displayValue(profileSummary.establishmentYear) }}</strong>
            </article>
            <article class="info-item info-item--wide">
              <span class="info-item__label">Address</span>
              <strong>{{ displayValue(profileSummary.address) }}</strong>
            </article>
          </div>
        </section>

        <section class="profile-card">
          <div class="section-heading">
            <p class="section-kicker">Edit</p>
            <h2>Update account</h2>
          </div>

          <form class="profile-form" @submit.prevent="saveProfile">
            <label class="field-block">
              <span class="field-label">Username</span>
              <input
                v-model.trim="form.username"
                type="text"
                class="field-input"
                placeholder="Enter your username"
              />
            </label>

            <label class="field-block">
              <span class="field-label">Email</span>
              <input
                v-model.trim="form.email"
                type="email"
                class="field-input"
                placeholder="Enter your email address"
              />
            </label>

            <label class="field-block">
              <span class="field-label">Current password</span>
              <input
                v-model="form.oldPassword"
                type="password"
                class="field-input"
                placeholder="Enter your current password"
              />
            </label>

            <label class="field-block">
              <span class="field-label">New password</span>
              <input
                v-model="form.password"
                type="password"
                class="field-input"
                placeholder="Leave blank to keep current password"
              />
            </label>

            <label class="field-block">
              <span class="field-label">Confirm new password</span>
              <input
                v-model="form.confirmPassword"
                type="password"
                class="field-input"
                placeholder="Re-enter the new password"
              />
            </label>

            <div class="form-note">
              Password updates are optional. If you enter a new password, provide your current
              password and make sure the confirmation matches before saving.
            </div>

            <div class="form-actions">
              <button
                type="button"
                class="action-button action-button--ghost"
                :disabled="isSubmitting"
                @click="resetForm"
              >
                Reset
              </button>
              <button
                type="submit"
                class="action-button action-button--primary"
                :disabled="isSubmitting"
              >
                {{ isSubmitting ? 'Saving...' : 'Save changes' }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getSingleOrganizations } from '../config/api-modules/api.organization';
import {
  getCurrentProfile,
  updateOrganizationLoginDetails,
} from '../config/api-modules/api.profile';

export default {
  name: 'ProfilePage',
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      profile: null,
      organizationRecord: null,
      form: {
        username: '',
        email: '',
        oldPassword: '',
        password: '',
        confirmPassword: '',
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['user']),

    profileSummary() {
      const source = this.profile || {};
      const regularUserProfile = source.regular_user_profile || {};
      const organizationProfile = source.organization_profile || {};
      const fullName =
        [source.first_name, source.last_name].filter(Boolean).join(' ') ||
        organizationProfile?.name ||
        source.full_name ||
        this.user?.full_name ||
        '';

      return {
        id: source.id || this.user?.id || null,
        fullName,
        username: source.username || this.user?.username || '',
        email: source.email || this.user?.email || '',
        organization:
          this.organizationRecord?.name ||
          organizationProfile?.name ||
          this.user?.organization?.name ||
          this.user?.organization ||
          '',
        position:
          regularUserProfile?.position ||
          organizationProfile?.position ||
          source.position ||
          this.user?.position ||
          '',
        role: source.type_display || this.getRoleLabel(source.type || this.user?.type),
        organizationType: organizationProfile?.type || regularUserProfile?.intititution_type || '',
        phoneNumber: organizationProfile?.phone_number || regularUserProfile?.phonenumber || '',
        country: regularUserProfile?.country || 'Nigeria',
        state: organizationProfile?.state || regularUserProfile?.state || '',
        geoZone: organizationProfile?.geo_zone || '',
        status: organizationProfile?.status || '',
        fileNumber: organizationProfile?.file_no || '',
        establishmentYear: organizationProfile?.establishment_year || '',
        organizationFocus: organizationProfile?.organization_focus || '',
        address: organizationProfile?.address || regularUserProfile?.address || '',
      };
    },
  },
  mounted() {
    this.loadProfile();
  },
  methods: {
    displayValue(value) {
      return value === null || value === undefined || value === '' ? 'Not Provided' : value;
    },
    getRoleLabel(type) {
      if (type === 1) {
        return 'Super Admin';
      }
      if (type === 2) {
        return 'NGO Admin';
      }
      if (type === 5) {
        return 'Organization User';
      }
      return 'Admin User';
    },
    resetForm() {
      this.form.username = this.profileSummary.username;
      this.form.email = this.profileSummary.email;
      this.form.oldPassword = '';
      this.form.password = '';
      this.form.confirmPassword = '';
    },
    getOrganizationId() {
      return (
        this.profile?.organization_id ||
        this.profile?.organization_profile?.id ||
        this.user?.organization_id ||
        this.user?.organization?.id ||
        null
      );
    },
    async loadOrganization() {
      const organizationId = this.getOrganizationId();

      if (!organizationId) {
        this.organizationRecord = null;
        return;
      }

      try {
        const response = await getSingleOrganizations(organizationId);
        this.organizationRecord = response.data?.data || response.data || null;
      } catch (error) {
        this.organizationRecord = null;
      }
    },
    async loadProfile() {
      this.isLoading = true;
      try {
        const response = await getCurrentProfile();
        this.profile = response.data?.data || response.data || null;
        await this.loadOrganization();
        this.resetForm();
      } catch (error) {
        this.profile = null;
        this.organizationRecord = null;
        this.$swal({
          title: 'Error',
          text: 'Unable to load your profile right now.',
          icon: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },
    validateForm() {
      if (!this.form.username) {
        return 'Username is required.';
      }

      if (!this.form.email) {
        return 'Email is required.';
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(this.form.email)) {
        return 'Enter a valid email address.';
      }

      if (this.form.password && !this.form.oldPassword) {
        return 'Current password is required to set a new password.';
      }

      if (this.form.password && this.form.password.length < 8) {
        return 'Password must be at least 8 characters.';
      }

      if (this.form.password !== this.form.confirmPassword) {
        return 'Password confirmation does not match.';
      }

      return '';
    },
    buildPayload() {
      const payload = {
        username: this.form.username,
        email: this.form.email,
      };

      if (this.form.password) {
        payload.old_password = this.form.oldPassword;
        payload.password = this.form.password;
      }

      return payload;
    },
    updateStoredUser() {
      const storedUser = this.user ? { ...this.user } : {};

      storedUser.first_name = this.profile?.first_name || storedUser.first_name || '';
      storedUser.last_name = this.profile?.last_name || storedUser.last_name || '';
      storedUser.username = this.form.username;
      storedUser.email = this.form.email;

      localStorage.setItem('user', JSON.stringify(storedUser));
      this.$store.commit('auth/setUser', storedUser);
    },
    async saveProfile() {
      const validationError = this.validateForm();

      if (validationError) {
        this.$swal({
          title: 'Validation error',
          text: validationError,
          icon: 'error',
        });
        return;
      }

      this.isSubmitting = true;
      try {
        await updateOrganizationLoginDetails(this.buildPayload());
        this.profile = {
          ...(this.profile || {}),
          username: this.form.username,
          email: this.form.email,
        };
        this.updateStoredUser();
        this.resetForm();
        this.$swal({
          title: 'Success',
          text: 'Your profile has been updated successfully.',
          icon: 'success',
        });
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: 'Unable to save your profile changes.',
          icon: 'error',
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-page {
  min-height: 100%;
}

.profile-shell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.profile-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.6rem 1.8rem;
  border-radius: 28px;
  background: radial-gradient(circle at top right, rgba(48, 171, 76, 0.18), transparent 28%),
    linear-gradient(135deg, #183523 0%, #1f5d35 54%, #2c8d4a 100%);
  color: #f4fff6;
}

.profile-eyebrow,
.section-kicker,
.info-item__label,
.field-label {
  display: block;
  margin-bottom: 0.45rem;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.profile-hero h1,
.section-heading h2 {
  margin: 0;
}

.profile-copy {
  max-width: 42rem;
  margin: 0.75rem 0 0;
  color: rgba(244, 255, 246, 0.82);
  line-height: 1.7;
}

.profile-hero__badge {
  min-width: 16rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.profile-hero__badge span {
  display: block;
  margin-bottom: 0.35rem;
  color: rgba(244, 255, 246, 0.72);
}

.loading-shell {
  padding: 2rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  text-align: center;
}

.profile-grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 1.5rem;
}

.profile-card {
  padding: 1.5rem;
  border: 1px solid rgba(29, 70, 44, 0.08);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(246, 250, 247, 0.94));
  box-shadow: 0 12px 32px rgba(21, 55, 33, 0.05);
}

.section-heading {
  margin-bottom: 1rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.info-item {
  padding: 1rem;
  border: 1px solid rgba(29, 70, 44, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.8);
}

.info-item strong {
  color: #163322;
  line-height: 1.6;
  word-break: break-word;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field-block {
  display: block;
}

.field-input {
  width: 100%;
  min-height: 3.25rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(29, 70, 44, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  color: #153021;
}

.field-input:focus {
  outline: none;
  border-color: rgba(48, 171, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.12);
}

.form-note {
  padding: 0.95rem 1rem;
  border-radius: 16px;
  background: #eef7f0;
  color: #466052;
  line-height: 1.6;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.9rem;
  margin-top: 0.5rem;
}

.action-button {
  min-width: 9.5rem;
  min-height: 3.1rem;
  padding: 0.75rem 1.1rem;
  border: 0;
  border-radius: 16px;
  font-weight: 700;
}

.action-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.action-button--primary {
  background: linear-gradient(135deg, #173721 0%, #30ab4c 100%);
  color: #fff;
}

.action-button--ghost {
  background: rgba(20, 38, 26, 0.08);
  color: #173721;
}

@media (max-width: 991px) {
  .profile-hero,
  .profile-grid,
  .info-grid {
    grid-template-columns: 1fr;
  }

  .profile-hero {
    align-items: stretch;
    flex-direction: column;
  }

  .profile-hero__badge {
    min-width: 0;
  }
}

@media (max-width: 640px) {
  .profile-card,
  .profile-hero {
    padding: 1.2rem;
    border-radius: 20px;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .action-button {
    width: 100%;
  }
}
</style>
